'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import { DefaultRoleSyncStrategy } from '@/services/auth/roleSyncStrategy'
import { USER_IDENTITY_SET_EVENT } from '@/utilities/client/userIdentityEvent'
import userService from '@/utilities/axios/api/user'
import {
  fetchTrustedIdentityScopes,
  getCachedTrustedIdentityScopes,
  setCachedTrustedIdentityScopesFromIdentity,
} from '@/utilities/auth/trustedIdentityScopes'

export function useIsSuperAdmin(): {
  isSuperAdmin: boolean
  isSuperAdminCheckReady: boolean
} {
  type IdentityScopes = IdentityInfoSchemaType['scopes']
  const cachedScopes = getCachedTrustedIdentityScopes()
  const [isSuperAdmin, setIsSuperAdmin] = useState(() =>
    new DefaultRoleSyncStrategy().isAshAdmin(cachedScopes ?? {}),
  )
  const [isSuperAdminCheckReady, setIsSuperAdminCheckReady] = useState(() => cachedScopes !== null)
  const latestRequestRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const applyScopes = useCallback((scopes: IdentityScopes) => {
    const roleSync = new DefaultRoleSyncStrategy()
    setIsSuperAdmin(roleSync.isAshAdmin(scopes))
    setIsSuperAdminCheckReady(true)
  }, [])

  const evaluateSuperAdmin = useCallback(
    (options?: { identity?: IdentityInfoSchemaType }) => {
      const requestId = ++latestRequestRef.current
      const providedIdentity = options?.identity

      if (providedIdentity !== undefined) {
        applyScopes(setCachedTrustedIdentityScopesFromIdentity(providedIdentity))
        return
      }

      const cachedScopes = getCachedTrustedIdentityScopes()
      if (cachedScopes) {
        applyScopes(cachedScopes)
        return
      }

      setIsSuperAdminCheckReady(false)

      void fetchTrustedIdentityScopes(() => userService.getIdentityInfo())
        .then((scopes) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) {
            return
          }
          applyScopes(scopes)
        })
        .catch((error) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) {
            return
          }
          console.error('Error checking super-admin status:', error)
          setIsSuperAdmin(false)
          setIsSuperAdminCheckReady(true)
        })
    },
    [applyScopes],
  )

  useEffect(() => {
    evaluateSuperAdmin()
  }, [evaluateSuperAdmin])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleUserIdentitySet = (event: Event) => {
      const identity = (event as CustomEvent<{ identity?: IdentityInfoSchemaType } | undefined>)
        .detail?.identity
      evaluateSuperAdmin(identity === undefined ? undefined : { identity })
    }

    window.addEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
    return () => window.removeEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
  }, [evaluateSuperAdmin])

  return { isSuperAdmin, isSuperAdminCheckReady }
}
